import React, { useContext, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { ExternalDropZone, Upload, UploadFileStatus } from "@progress/kendo-react-upload";
import { useOktaAuth } from "@okta/okta-react";
import { UserContext } from "shared/context/UserContext";

import {
  ACCEPTED_835_FILE_EXTENSIONS,
  ACCEPTED_835_FILE_EXTENSIONS_DISPLAY,
  REJECTED_835_FILE_TYPES_MESSAGE,
  MAX_835_FILE_SIZE_BYTES,
  MIN_835_FILE_SIZE_BYTES,
  MAX_835_FILE_COUNT,
  FILE_835_LIMITS_MESSAGE,
  TOO_MANY_835_FILES_MESSAGE,
  UPLOAD_835_HEADING,
  UPLOAD_835_HELP_TEXT,
  UPLOAD_835_HELP_LABEL,
  REMOVE_ALL_835_FILES_LABEL,
  REJECTED_835_FILES_HEADING,
  SAVE_835_FILES_URL,
  SUBMIT_835_FILES_LABEL,
  SUBMITTING_835_FILES_LABEL,
  SUBMITTED_835_FILES_MESSAGE,
  FAILED_835_FILES_HEADING,
  FAILED_835_FILE_MESSAGE_SUFFIX,
  NO_835_ORGANIZATION_MESSAGE,
  UPLOAD_HISTORY_835_HEADING,
  UPLOAD_HISTORY_835_HELP_TEXT,
  UPLOAD_HISTORY_835_HELP_LABEL,
  UPLOAD_HISTORY_835_NOTE,
  UPLOAD_HISTORY_835_LOADING_MESSAGE,
  UPLOAD_HISTORY_835_ERROR_MESSAGE,
  UPLOAD_HISTORY_835_EMPTY_MESSAGE,
  UPLOAD_HISTORY_835_NO_ORGANIZATION_MESSAGE,
} from "./constants/ImportConversion835Constants";
import { getFileValidationMessages } from "./utils/getFileValidationMessages";
import { getUploadResultPresentation } from "./utils/getUploadResultPresentation";
import { useFetchImportConversion835UploadHistory } from "./hooks/useFetchImportConversion835UploadHistory";
import InfoTooltip from "./components/InfoTooltip";
import Faq835Section from "./components/Faq835Section";

import "./ImportConversion835.css";

const UPLOAD_RESTRICTIONS = {
  allowedExtensions: ACCEPTED_835_FILE_EXTENSIONS,
  maxFileSize: MAX_835_FILE_SIZE_BYTES,
  minFileSize: MIN_835_FILE_SIZE_BYTES,
};

/**
 * 835 Import & Conversion file upload.
 *
 * The Kendo Upload is used in controlled mode with `autoUpload` and `showActionButtons`
 * disabled so it only collects and validates the user's selection - it never posts on its own.
 * Submission is driven by the "Submit Files" button below, which calls the Upload's
 * `triggerUpload` method so the files are sent through Kendo's own transport. This mirrors how
 * Missing Remittance attachments are uploaded (see MissingRemittanceAddEditModal.js), with the
 * bearer token injected per request by `onBeforeUpload`.
 *
 * `batch` is false so each file is sent as its own request. Keep it that way: batching every
 * file into a single request would make the body up to MAX_835_FILE_COUNT x
 * MAX_835_FILE_SIZE_BYTES and breach the host request-size limits that a single 25 MB file
 * stays comfortably under.
 */
export default function ImportConversion835() {
  const uploadRef = useRef(null);
  const { oktaAuth } = useOktaAuth();
  const userConfig = useContext(UserContext);
  const [files, setFiles] = useState([]);

  /**
   * The organization currently selected in the application header.
   *
   * Every upload is recorded against this selection, which is why both the id and its type are
   * sent. NOTE (NETX-5821): `currentOrganization.value` is the GROUP id for a Group user, not an
   * RPID, and the two cannot be used interchangeably - the server stores the pair rather than
   * assuming an RPID.
   */
  const currentOrganization = userConfig && userConfig.currentOrganization;

  const hasOrganization = Boolean(
    currentOrganization && currentOrganization.value && currentOrganization.type
  );

  function handleFileStateChange(event) {
    console.log(event);
    console.log(event.newState);
    setFiles(event.newState);
  }

  /**
   * Headers applied to every upload request.
   *
   * Unlike MissingRemittanceAddEditModal.js this deliberately does not send an
   * "Access-Control-Allow-Origin" header: that is a response header, so setting it on the
   * request achieves nothing and only risks the browser failing the preflight.
   */
  function saveHeaders() {
    return {
      Authorization: `Bearer ${oktaAuth.getAccessToken()}`,
    };
  }

  /**
   * The Upload is controlled through its `files` prop, so emptying our state clears the
   * rendered list too - no imperative call into the Kendo instance is needed.
   */
  function handleRemoveAll() {
    setFiles([]);
  }

  /**
   * Hands the selected files to the Kendo Upload, which posts each one to SAVE_835_FILES_URL.
   * Progress and per-file outcomes come back through `onStatusChange`.
   */
  function handleSubmit() {
    if (uploadRef.current) {
      uploadRef.current.triggerUpload();
    }
  }

  const hasInvalidFile = files.some(
    (file) => file.validationErrors && file.validationErrors.length > 0
  );

  const exceedsFileCount = files.length > MAX_835_FILE_COUNT;

  const isUploading = files.some(
    (file) => file.status === UploadFileStatus.Uploading
  );

  const failedFiles = files.filter(
    (file) => file.status === UploadFileStatus.UploadFailed
  );

  /**
   * Files the Upload will actually send when `triggerUpload` runs.
   *
   * This mirrors Kendo's own `filesForUpload` rule - only files still in the `Selected` state
   * and free of validation errors get posted. Anything already uploaded, in flight, or
   * previously failed is skipped, so basing the Submit button on a looser condition would
   * leave it enabled but inert. A failed file is re-sent through its own retry action rather
   * than through Submit.
   */
  const uploadableFiles = files.filter(
    (file) =>
      file.status === UploadFileStatus.Selected &&
      !(file.validationErrors && file.validationErrors.length > 0)
  );

  const allFilesUploaded =
    files.length > 0 &&
    files.every((file) => file.status === UploadFileStatus.Uploaded);

  const uploadedFileCount = files.filter(
    (file) => file.status === UploadFileStatus.Uploaded
  ).length;

  const {
    data: uploadHistory,
    isLoading: isUploadHistoryLoading,
    isError: isUploadHistoryError,
    refetch: refetchUploadHistory,
  } = useFetchImportConversion835UploadHistory(currentOrganization);

  /**
   * Pulls the history back once a submission has finished.
   *
   * `batch` is false, so each file is its own request and there is no single "upload finished"
   * event to hang this off. Waiting for every file to succeed would be wrong: if three files
   * upload and one fails, the three rows that were written would never appear. Waiting instead
   * for the batch to settle - nothing still in flight, and at least one file through - covers
   * the partial-failure case and fires once per submission rather than once per file.
   */
  useEffect(() => {
    if (isUploading || uploadedFileCount === 0) {
      return;
    }

    refetchUploadHistory();
  }, [isUploading, uploadedFileCount, refetchUploadHistory]);

  const canSubmit =
    uploadableFiles.length > 0 &&
    !hasInvalidFile &&
    !exceedsFileCount &&
    !isUploading &&
    hasOrganization;

  const rejectedFileMessages = getFileValidationMessages(files);

  return (
    <div className="feature">
      <div className="import-conversion-835-upload">
        <div className="import-conversion-835-upload-heading">
          {UPLOAD_835_HEADING}
          <InfoTooltip
            id="import-conversion-835-upload-help"
            label={UPLOAD_835_HELP_LABEL}
            text={UPLOAD_835_HELP_TEXT}
          />
        </div>

        <ExternalDropZone
          uploadRef={uploadRef}
          customHint="Drag & drop your 835 files here"
          customNote="or use the Select files button below to browse from your computer"
        />

        <Upload
          ref={uploadRef}
          batch={false}
          multiple={true}
          autoUpload={false}
          showActionButtons={false}
          withCredentials={false}
          saveUrl={SAVE_835_FILES_URL}
          files={files}
          onAdd={handleFileStateChange}
          onRemove={handleFileStateChange}
          onStatusChange={handleFileStateChange}
          onBeforeUpload={(event) => {
            event.headers = {
              ...event.headers,
              ...saveHeaders(),
            };

            // Kendo appends `additionalData` to the multipart body, so the header dropdown
            // selection travels with the file without changing the transport.
            event.additionalData = {
              ...event.additionalData,
              organizationId: currentOrganization ? currentOrganization.value : "",
              organizationType: currentOrganization ? currentOrganization.type : "",
            };
          }}
          restrictions={UPLOAD_RESTRICTIONS}
        />

        <div className="import-conversion-835-accepted-types">
          {"\u2714"} Accepted formats:{" "}
          <strong>{ACCEPTED_835_FILE_EXTENSIONS_DISPLAY}</strong>
        </div>
        <div className="import-conversion-835-rejected-types">
          {"\u2716"} {REJECTED_835_FILE_TYPES_MESSAGE}
        </div>
        <div className="import-conversion-835-limits">
          {FILE_835_LIMITS_MESSAGE}
        </div>

        {exceedsFileCount && (
          <div
            className="import-conversion-835-file-count-error"
            role="alert"
          >
            {TOO_MANY_835_FILES_MESSAGE}
          </div>
        )}

        {files.length > 0 && !hasOrganization && (
          <div
            className="import-conversion-835-file-count-error"
            role="alert"
          >
            {NO_835_ORGANIZATION_MESSAGE}
          </div>
        )}

        {rejectedFileMessages.length > 0 && (
          <div className="import-conversion-835-rejected-files" role="alert">
            <div className="import-conversion-835-rejected-files-heading">
              {REJECTED_835_FILES_HEADING}
            </div>
            <ul className="import-conversion-835-rejected-files-list">
              {rejectedFileMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        )}

        {failedFiles.length > 0 && (
          <div className="import-conversion-835-failed-files" role="alert">
            <div className="import-conversion-835-failed-files-heading">
              {FAILED_835_FILES_HEADING}
            </div>
            <ul className="import-conversion-835-failed-files-list">
              {failedFiles.map((file) => (
                <li key={file.uid}>
                  {file.name} {FAILED_835_FILE_MESSAGE_SUFFIX}
                </li>
              ))}
            </ul>
          </div>
        )}

        {allFilesUploaded && (
          <div className="import-conversion-835-submitted" role="status">
            {"\u2714"} {SUBMITTED_835_FILES_MESSAGE}
          </div>
        )}

        <div className="import-conversion-835-actions">
          {files.length > 0 && (
            <Button
              variant="outline-secondary"
              className="import-conversion-835-remove-all"
              onClick={handleRemoveAll}
              disabled={isUploading}
            >
              {REMOVE_ALL_835_FILES_LABEL}
            </Button>
          )}

          <Button
            className="import-conversion-835-submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isUploading ? SUBMITTING_835_FILES_LABEL : SUBMIT_835_FILES_LABEL}
          </Button>
        </div>

        <div className="import-conversion-835-history">
          <div className="import-conversion-835-history-heading">
            {UPLOAD_HISTORY_835_HEADING}
            <InfoTooltip
              id="import-conversion-835-history-help"
              label={UPLOAD_HISTORY_835_HELP_LABEL}
              text={UPLOAD_HISTORY_835_HELP_TEXT}
            />
          </div>
          <div className="import-conversion-835-history-note">
            {UPLOAD_HISTORY_835_NOTE}
          </div>

          {!hasOrganization && (
            <div className="import-conversion-835-history-message">
              {UPLOAD_HISTORY_835_NO_ORGANIZATION_MESSAGE}
            </div>
          )}

          {hasOrganization && isUploadHistoryLoading && (
            <div className="import-conversion-835-history-message">
              {UPLOAD_HISTORY_835_LOADING_MESSAGE}
            </div>
          )}

          {hasOrganization && !isUploadHistoryLoading && isUploadHistoryError && (
            <div
              className="import-conversion-835-history-error"
              role="alert"
            >
              {UPLOAD_HISTORY_835_ERROR_MESSAGE}
            </div>
          )}

          {hasOrganization &&
            !isUploadHistoryLoading &&
            !isUploadHistoryError &&
            (!uploadHistory || uploadHistory.length === 0) && (
              <div className="import-conversion-835-history-message">
                {UPLOAD_HISTORY_835_EMPTY_MESSAGE}
              </div>
            )}

          {hasOrganization &&
            !isUploadHistoryLoading &&
            !isUploadHistoryError &&
            uploadHistory &&
            uploadHistory.length > 0 && (
              <div className="import-conversion-835-results-scroll">
                {uploadHistory.map((item) => {
                  const result = getUploadResultPresentation(item);

                  return (
                    <div
                      key={item.uploadHistoryId}
                      className={`import-conversion-835-result ${result.statusClass}`}
                    >
                      <div
                        className="import-conversion-835-result-icon"
                        aria-hidden="true"
                      >
                        {result.icon}
                      </div>
                      <div className="import-conversion-835-result-text">
                        <strong>{result.title}</strong>
                        {result.message}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </div>

      <Faq835Section />
    </div>
  );
}
